import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Minus, Plus, ArrowRight, ArrowLeft, Plus as PlusIcon, X, Gift, Truck } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { REVIVE_FLAVOURS } from "../brand/flavours";
import { computeCart, REVIVE_UNIT, ORYVITA_UNIT } from "@/lib/pricing";
import { CheckoutSupportStrip } from "./CheckoutSupportStrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMIRATES = [
  { value: "abu-dhabi", label: "Abu Dhabi" }, { value: "dubai", label: "Dubai" },
  { value: "sharjah", label: "Sharjah" }, { value: "ajman", label: "Ajman" },
  { value: "umm-al-quwain", label: "Umm Al Quwain" },
  { value: "ras-al-khaimah", label: "Ras Al Khaimah" }, { value: "fujairah", label: "Fujairah" },
];
const emptyAddress = { name: "", phone: "", email: "", emirate: "dubai", city: "", address_line1: "", address_line2: "", notes: "" };

/** Multi-flavor REVIVE cart with cross-sell → shipping → Ziina */
export const ReviveBuyDialog = ({ open, onOpenChange, initialFlavour }) => {
  const [step, setStep] = useState(1); // 1=cart, 2=cross-sell, 3=shipping
  const [lines, setLines] = useState([]); // {flavor, qty}
  const [oryvitaKg, setOryvitaKg] = useState(0);
  const [address, setAddress] = useState(emptyAddress);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setLines([{ flavor: initialFlavour || REVIVE_FLAVOURS[0].name, qty: 1 }]);
      setOryvitaKg(0);
    }
  }, [open, initialFlavour]);

  const reviveBoxes = lines.reduce((s, l) => s + l.qty, 0);
  const cart = computeCart(reviveBoxes, oryvitaKg);

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const removeLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));
  const addLine = () => {
    const used = new Set(lines.map((l) => l.flavor));
    const next = REVIVE_FLAVOURS.find((f) => !used.has(f.name)) || REVIVE_FLAVOURS[0];
    setLines((ls) => [...ls, { flavor: next.name, qty: 1 }]);
  };

  const onAddr = (e) => setAddress((a) => ({ ...a, [e.target.name]: e.target.value }));
  const validate = () => {
    if (reviveBoxes < 1) return "Add at least one REVIVE box.";
    const req = ["name", "phone", "email", "emirate", "city", "address_line1"];
    for (const k of req) if (!String(address[k] || "").trim()) return `Please fill in ${k.replace("_", " ")}.`;
    if (!/^\S+@\S+\.\S+$/.test(address.email)) return "Please enter a valid email address.";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) return toast.error(err);
    const items = lines.filter((l) => l.qty > 0).map((l) => ({ product_id: "field-salts", flavor: l.flavor, quantity: l.qty }));
    if (oryvitaKg > 0) items.push({ product_id: "shirataki-rice", quantity: oryvitaKg });
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/checkout`, {
        items, shipping: {
          name: address.name.trim(), phone: address.phone.trim(), email: address.email.trim(),
          emirate: address.emirate, city: address.city.trim(),
          address_line1: address.address_line1.trim(),
          address_line2: address.address_line2.trim() || undefined,
          notes: address.notes.trim() || undefined,
        },
      });
      if (data?.redirect_url) window.location.assign(data.redirect_url);
      else toast.error("Could not start checkout.");
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Checkout is not available right now.");
    } finally { setLoading(false); }
  };

  const bundleNote = reviveBoxes === 1
    ? "Add another box to save AED 20."
    : reviveBoxes === 2 ? "Bundle applied — AED 300 for 2 boxes (save AED 20)."
    : reviveBoxes >= 3 ? `10% off applied — you saved AED ${cart.reviveSavings.toFixed(2)}.`
    : "Add at least one box.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="revive-buy-dialog" className="max-w-2xl w-[calc(100vw-2rem)] bg-bone text-ink border-hairline p-0 overflow-hidden">
        <div className="bg-ink text-bone px-6 md:px-8 py-6">
          <DialogHeader className="space-y-2 text-left">
            <div className="overline text-terracotta">REVIVE · Daily Electrolytes · Step {step} of 3</div>
            <DialogTitle className="font-display font-black text-3xl md:text-4xl tracking-tight">
              {step === 1 ? "Build your box." : step === 2 ? "Before you go —" : "Where should we send it?"}
            </DialogTitle>
            <DialogDescription className="text-bone/70 text-sm">
              1 box AED 160 · 2 boxes AED 300 · 3+ boxes 10% off · 30 sachets each · No sugar, carbs, calories.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 md:px-8 py-7 max-h-[calc(100vh-260px)] overflow-y-auto">
          {step === 1 && (
            <>
              <div data-testid="revive-bundle-banner" className={`flex items-start gap-3 border rounded-sm px-4 py-3 mb-6 ${reviveBoxes>=2?"border-terracotta bg-terracotta/8":"border-hairline bg-surface"}`}>
                <Gift className={`w-5 h-5 mt-0.5 shrink-0 ${reviveBoxes>=2?"text-terracotta":"text-inkmute"}`} strokeWidth={1.75}/>
                <div>
                  <div className="overline text-terracotta">Bundle pricing</div>
                  <p className="text-sm text-ink/85 mt-1 leading-snug">{bundleNote}</p>
                </div>
              </div>

              <div className="overline text-inkmute mb-3">Your boxes</div>
              <div className="space-y-3" data-testid="revive-cart-lines">
                {lines.map((line, i) => {
                  const swatch = REVIVE_FLAVOURS.find((f) => f.name === line.flavor)?.color || "#ccc";
                  return (
                    <div key={i} data-testid={`revive-line-${i}`} className="flex items-center gap-3 border border-hairline bg-surface rounded-sm px-3 py-2">
                      <span className="w-5 h-5 rounded-full border border-hairline shrink-0" style={{backgroundColor:swatch}}/>
                      <select value={line.flavor} onChange={(e)=>setLine(i,{flavor:e.target.value})} data-testid={`revive-line-flavour-${i}`} className="flex-1 bg-transparent outline-none font-display font-semibold text-ink text-sm py-2">
                        {REVIVE_FLAVOURS.map((f)=>(<option key={f.name} value={f.name}>{f.name}{f.badge?" · NEW":""}</option>))}
                      </select>
                      <div className="inline-flex items-center border border-hairline rounded-sm">
                        <button type="button" onClick={()=>setLine(i,{qty:Math.max(1,line.qty-1)})} className="px-2 py-2 hover:bg-surfacealt disabled:opacity-30" disabled={line.qty<=1} data-testid={`revive-line-dec-${i}`}><Minus className="w-3.5 h-3.5"/></button>
                        <span className="font-display font-bold text-sm w-8 text-center" data-testid={`revive-line-qty-${i}`}>{line.qty}</span>
                        <button type="button" onClick={()=>setLine(i,{qty:Math.min(30,line.qty+1)})} className="px-2 py-2 hover:bg-surfacealt" data-testid={`revive-line-inc-${i}`}><Plus className="w-3.5 h-3.5"/></button>
                      </div>
                      {lines.length > 1 && (
                        <button type="button" onClick={()=>removeLine(i)} className="p-2 text-inkmute hover:text-terracotta" data-testid={`revive-line-remove-${i}`} aria-label="Remove"><X className="w-4 h-4"/></button>
                      )}
                    </div>
                  );
                })}
              </div>
              {lines.length < REVIVE_FLAVOURS.length && (
                <button type="button" onClick={addLine} data-testid="revive-add-flavour" className="mt-4 inline-flex items-center gap-2 overline text-ink hover:text-terracotta link-underline">
                  <PlusIcon className="w-4 h-4"/> Add another flavour
                </button>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-7 pt-6 border-t border-hairline">
                <div>
                  <div className="overline text-inkmute mb-1">Boxes</div>
                  <div className="font-display font-bold text-2xl text-ink" data-testid="revive-total-boxes">{reviveBoxes}</div>
                  <div className="text-xs text-inkmute mt-1">{reviveBoxes * 30} sachets</div>
                </div>
                <div className="sm:text-right">
                  <div className="overline text-inkmute mb-1">REVIVE subtotal</div>
                  <div className="font-display font-black text-2xl text-ink" data-testid="revive-subtotal">AED {cart.reviveSubtotal.toFixed(2)}</div>
                  {cart.reviveSavings > 0 && <div className="text-xs text-terracotta font-semibold mt-1">Saved AED {cart.reviveSavings.toFixed(2)}</div>}
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <div data-testid="revive-crosssell" className="space-y-5">
              <p className="text-ink/80 text-base leading-relaxed">
                <span className="font-wordmark italic text-terracotta">…have you tried Oryvita?</span> The featherlight rice alternative — a smart pairing with your electrolytes.
              </p>
              <div className="border border-hairline bg-surface rounded-sm p-4 md:p-5">
                <div className="flex items-baseline justify-between mb-3">
                  <div>
                    <div className="font-display font-bold text-ink text-lg">Oryvita — Dry Shirataki Rice</div>
                    <div className="text-xs text-inkmute mt-1">AED 49.95 / kg · 1 kg dry pack · 41 kcal / serving</div>
                  </div>
                  <div className="inline-flex items-center border border-hairline rounded-sm bg-bone">
                    <button type="button" onClick={()=>setOryvitaKg((v)=>Math.max(0,v-1))} disabled={oryvitaKg<=0} className="px-2 py-2 hover:bg-surfacealt disabled:opacity-30" data-testid="crosssell-oryvita-dec"><Minus className="w-3.5 h-3.5"/></button>
                    <span className="font-display font-bold text-sm w-10 text-center" data-testid="crosssell-oryvita-qty">{oryvitaKg}</span>
                    <button type="button" onClick={()=>setOryvitaKg((v)=>Math.min(20,v+1))} className="px-2 py-2 hover:bg-surfacealt" data-testid="crosssell-oryvita-inc"><Plus className="w-3.5 h-3.5"/></button>
                  </div>
                </div>
                {oryvitaKg > 0 ? (
                  <div className="text-sm text-terracotta font-display font-semibold">✦ Added {oryvitaKg} kg — AED {(oryvitaKg*ORYVITA_UNIT).toFixed(2)}</div>
                ) : (
                  <button type="button" onClick={()=>setOryvitaKg(1)} data-testid="crosssell-oryvita-add-1" className="text-sm font-display font-semibold text-terracotta hover:text-ink link-underline">
                    Add 1 kg for AED 49.95
                  </button>
                )}
              </div>
              <div className="text-xs text-inkmute">No thanks? Just click Continue — we'll pack REVIVE only.</div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="revive-shipping-form" className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <F label="Full name" name="name" value={address.name} onChange={onAddr} placeholder="Jane Smith" tid="shipping-name"/>
                <F label="Phone" name="phone" value={address.phone} onChange={onAddr} placeholder="+971 50 000 0000" tid="shipping-phone"/>
              </div>
              <F label="Email" name="email" type="email" value={address.email} onChange={onAddr} placeholder="you@domain.com" tid="shipping-email"/>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="overline text-inkmute block mb-2">Emirate</label>
                  <select name="emirate" value={address.emirate} onChange={onAddr} data-testid="shipping-emirate" className="w-full bg-surface border border-hairline focus:border-ink outline-none py-3 px-3 text-ink text-base rounded-sm">
                    {EMIRATES.map((e)=>(<option key={e.value} value={e.value}>{e.label}</option>))}
                  </select>
                </div>
                <F label="City / Area" name="city" value={address.city} onChange={onAddr} placeholder="e.g. Business Bay" tid="shipping-city"/>
              </div>
              <F label="Building / Villa / Street" name="address_line1" value={address.address_line1} onChange={onAddr} placeholder="Building name, villa no., street" tid="shipping-address1"/>
              <F label="Apartment / Floor (optional)" name="address_line2" value={address.address_line2} onChange={onAddr} placeholder="Apt / floor / unit" tid="shipping-address2"/>
              <div>
                <label className="overline text-inkmute block mb-2">Delivery notes (optional)</label>
                <textarea name="notes" rows={2} value={address.notes} onChange={onAddr} data-testid="shipping-notes" placeholder="Gate code, landmark, best time…" className="w-full bg-surface border border-hairline focus:border-ink outline-none py-3 px-3 text-ink text-base rounded-sm resize-none"/>
              </div>

              <div className="border-t border-hairline pt-5 mt-6" data-testid="order-summary">
                {reviveBoxes>0 && (
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-inkmute">REVIVE × {reviveBoxes} {reviveBoxes>=2 && <span className="text-terracotta font-semibold">· bundle</span>}</span>
                    <span className="text-ink font-semibold">AED {cart.reviveSubtotal.toFixed(2)}</span>
                  </div>
                )}
                {oryvitaKg>0 && (
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-inkmute">Oryvita × {oryvitaKg} kg</span>
                    <span className="text-ink font-semibold">AED {cart.oryvitaSubtotal.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm mb-3">
                  <span className="text-inkmute">Delivery</span>
                  <span className={`font-semibold ${cart.freeShipping?"text-terracotta":"text-ink"}`} data-testid="delivery-fee">{cart.freeShipping?"FREE":`AED ${cart.delivery.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between items-baseline pt-3 border-t border-hairline">
                  <span className="overline text-ink">Total</span>
                  <span data-testid="revive-total" className="font-display font-black text-2xl text-ink">AED {cart.total.toFixed(2)}</span>
                </div>
                <p className="text-[11px] text-inkmute mt-1 uppercase tracking-widest font-display font-semibold text-right">Incl VAT</p>
              </div>

              <div className="pt-4 border-t border-hairline">
                <div className="flex items-center gap-2 mb-2"><Truck className="w-4 h-4 text-terracotta"/><span className="overline text-ink">Delivery</span></div>
                <p className="text-xs text-inkmute leading-relaxed">UAE-wide 1–2 business days from Dubai · Free over AED 200 · AED 15 fee below · SMS + email tracking.</p>
              </div>
            </div>
          )}
        </div>

        <CheckoutSupportStrip />

        <div className="bg-surfacealt border-t border-hairline px-6 md:px-8 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {step > 1 ? (
            <button type="button" onClick={()=>setStep(step-1)} data-testid="revive-back" className="inline-flex items-center gap-2 text-ink hover:text-terracotta overline link-underline">
              <ArrowLeft className="w-4 h-4"/>Back
            </button>
          ) : <div className="text-xs text-inkmute">Secure UAE checkout · Powered by Ziina</div>}

          {step === 1 && (
            <button type="button" onClick={()=>setStep(2)} disabled={reviveBoxes<1} data-testid="revive-next-crosssell"
              className="inline-flex items-center gap-2 bg-terracotta hover:bg-ink transition-colors duration-300 text-white px-6 py-3.5 rounded-sm font-display font-semibold text-sm disabled:opacity-40">
              Continue — AED {cart.reviveSubtotal.toFixed(2)}<ArrowRight className="w-4 h-4"/>
            </button>
          )}
          {step === 2 && (
            <button type="button" onClick={()=>setStep(3)} data-testid="revive-next-shipping"
              className="inline-flex items-center gap-2 bg-terracotta hover:bg-ink transition-colors duration-300 text-white px-6 py-3.5 rounded-sm font-display font-semibold text-sm">
              Continue to delivery<ArrowRight className="w-4 h-4"/>
            </button>
          )}
          {step === 3 && (
            <button type="button" onClick={submit} disabled={loading} data-testid="revive-checkout-cta"
              className="inline-flex items-center gap-2 bg-terracotta hover:bg-ink transition-colors duration-300 text-white px-6 py-3.5 rounded-sm font-display font-semibold text-sm disabled:opacity-60">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin"/>Starting checkout…</> : <>Pay AED {cart.total.toFixed(2)} · Ziina<ArrowRight className="w-4 h-4"/></>}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const F = ({ label, name, value, onChange, placeholder, type="text", tid }) => (
  <div>
    <label htmlFor={name} className="overline text-inkmute block mb-2">{label}</label>
    <input id={name} name={name} type={type} value={value} onChange={onChange} placeholder={placeholder} data-testid={tid}
      className="w-full bg-surface border border-hairline focus:border-ink outline-none py-3 px-3 text-ink placeholder:text-inkmute/60 text-base rounded-sm"/>
  </div>
);
